import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { trackCandidate } from '../utils/recentCandidates';
import { APPLICANT_STATUS_OPTIONS, NOTE_REQUIRED_STATUSES, appendStatusNote, getApplicantStatusColor, getApplicantStatusLabel } from '../utils/applicantStatus';
import { ArrowLeftIcon, ChevronDownIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import WhatsAppButton from '../components/WhatsAppButton';
import StatusNoteModal from '../components/StatusNoteModal';
import HiredStatusModal from '../components/HiredStatusModal';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm';

const normalizePhoneInput = (rawValue, fallbackCountryCode = '+91') => {
  const sanitized = String(rawValue || '').replace(/[^\d+]/g, '');
  if (!sanitized) {
    return { countryCode: fallbackCountryCode, phone: '' };
  }

  const digitsOnly = sanitized.replace(/\D/g, '');
  if (!digitsOnly) {
    return { countryCode: fallbackCountryCode, phone: '' };
  }

  if (digitsOnly.length <= 10) {
    return { countryCode: fallbackCountryCode, phone: digitsOnly };
  }

  return {
    countryCode: `+${digitsOnly.slice(0, -10)}`,
    phone: digitsOnly.slice(-10)
  };
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '-'}</dd>
  </div>
);

const EditField = ({ label, name, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input type={type} name={name} value={value || ''} onChange={onChange} required={required} className={inputClass} />
  </div>
);

const ApplicantProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [activityLogs, setActivityLogs] = useState([]);
  const [pendingStatus, setPendingStatus] = useState('');
  const [showHiredModal, setShowHiredModal] = useState(false);
  const [positionOptions, setPositionOptions] = useState([]);

  useEffect(() => {
    fetchApplicant();
    fetchPositionOptions();
  }, [id]);

  const fetchApplicant = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/applicants/${id}`);
      setApplicant(response.data);
      trackCandidate(user?.id, response.data);
      // Fetch activity logs for super admin
      if (user?.isSuperAdmin) {
        try {
          const logsRes = await axios.get(`/api/applicants/${id}/activity-logs`);
          setActivityLogs(logsRes.data);
        } catch (logErr) {
          console.error('Error fetching activity logs:', logErr);
        }
      }
    } catch (error) {
      console.error('Error fetching applicant:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositionOptions = async () => {
    try {
      const response = await axios.get('/api/jobs?limit=1000');
      setPositionOptions(
        (response.data?.jobs || [])
          .map((job) => job.position)
          .filter(Boolean)
      );
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  const startEditing = () => {
    setFormData({
      name: applicant.name || '',
      countryCode: applicant.countryCode || '+91',
      phone: applicant.phone || '',
      countryCode2: applicant.countryCode2 || '+91',
      phone2: applicant.phone2 || '',
      email: applicant.email || '',
      position: applicant.position || '',
      leadSource: applicant.leadSource || '',
      status: applicant.status || 'new_lead',
      notes: applicant.notes || '',
      hireDate: applicant.hireDate ? applicant.hireDate.split('T')[0] : '',
      salary: applicant.salary || '',
      startDate: applicant.startDate ? applicant.startDate.split('T')[0] : '',
      department: applicant.department || '',
      employeeId: applicant.employeeId || ''
    });
    setEditing(true);
    setError('');
  };

  const cancelEditing = () => {
    setEditing(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' || name === 'phone2') {
      const countryField = name === 'phone' ? 'countryCode' : 'countryCode2';
      const { countryCode, phone } = normalizePhoneInput(value, formData[countryField] || '+91');
      setFormData(prev => ({
        ...prev,
        [countryField]: countryCode,
        [name]: phone
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...formData };
      if (payload.salary) payload.salary = parseFloat(payload.salary);
      if (!payload.hireDate) delete payload.hireDate;
      if (!payload.startDate) delete payload.startDate;
      if (!payload.salary) delete payload.salary;
      if (!payload.department) delete payload.department;
      if (!payload.employeeId) delete payload.employeeId;

      await axios.put(`/api/applicants/${id}`, payload);
      setEditing(false);
      fetchApplicant();
      trackCandidate(user?.id, { id: parseInt(id), ...payload });
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving applicant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.isSuperAdmin || deleting) return;
    if (!window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) return;

    setDeleting(true);
    setError('');

    try {
      await axios.delete(`/api/applicants/${id}`);
      navigate('/applicants');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting contact');
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!applicant || nextStatus === applicant.status || statusSaving) return;

    if (nextStatus === 'hired') {
      setShowHiredModal(true);
      setError('');
      return;
    }

    if (NOTE_REQUIRED_STATUSES.includes(nextStatus)) {
      setPendingStatus(nextStatus);
      setError('');
      return;
    }

    setStatusSaving(true);
    setError('');

    try {
      const response = await axios.put(`/api/applicants/${id}`, { status: nextStatus });
      setApplicant(response.data);
      trackCandidate(user?.id, response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleHiredSave = async (hiredData) => {
    if (!applicant) return;

    setStatusSaving(true);
    setError('');

    try {
      const response = await axios.put(`/api/applicants/${id}`, {
        status: 'hired',
        ...hiredData
      });
      setApplicant(response.data);
      setShowHiredModal(false);
      trackCandidate(user?.id, response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleStatusNoteSave = async (note) => {
    if (!pendingStatus || !applicant) return;

    setStatusSaving(true);
    setError('');

    try {
      const response = await axios.put(`/api/applicants/${id}`, {
        status: pendingStatus,
        notes: appendStatusNote(applicant.notes, pendingStatus, note)
      });
      setApplicant(response.data);
      setPendingStatus('');
      trackCandidate(user?.id, response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status');
    } finally {
      setStatusSaving(false);
    }
  };

  if (loading) {
    return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;
  }

  if (!applicant) {
    return <Layout><div className="text-center py-12 text-gray-500">Applicant not found.</div></Layout>;
  }

  const statusLabel = getApplicantStatusLabel(applicant.status || 'new_lead');
  const canUpdateStatus = hasPermission('applicant', 'update');

  const isHired = editing ? formData.status === 'hired' : applicant.status === 'hired';

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/applicants')} className="text-gray-500 hover:text-gray-700">
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{applicant.name}</h1>
            {editing || !canUpdateStatus ? (
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getApplicantStatusColor(applicant.status)}`}>
                {statusLabel}
              </span>
            ) : (
              <div className="relative">
                <select
                  aria-label="Applicant status"
                  value={applicant.status || 'new_lead'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={statusSaving}
                  className={`appearance-none rounded-full border border-transparent px-3 py-1 pr-8 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ${getApplicantStatusColor(applicant.status)}`}
                >
                  {APPLICANT_STATUS_OPTIONS.map((statusOption) => (
                    <option key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-current opacity-70" />
              </div>
            )}
          </div>
          {!editing ? (
            <button
              onClick={startEditing}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={cancelEditing}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {/* Contact Information */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Contact Information</h2>
          </div>
          <div className="px-6 py-5">
            {editing ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <EditField label="Name" name="name" value={formData.name} onChange={handleChange} required />
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+971">+971</option>
                      <option value="+65">+65</option>
                      <option value="+61">+61</option>
                    </select>
                    <input type="tel" name="phone" required pattern="\d{10}" placeholder="10 digits" value={formData.phone} onChange={handleChange} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone 2</label>
                  <div className="flex gap-2">
                    <select name="countryCode2" value={formData.countryCode2} onChange={handleChange} className="w-20 px-2 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+971">+971</option>
                      <option value="+65">+65</option>
                      <option value="+61">+61</option>
                    </select>
                    <input type="tel" name="phone2" pattern="\d{10}" placeholder="10 digits" value={formData.phone2 || ''} onChange={handleChange} className={inputClass} />
                  </div>
                </div>
                <EditField label="Email" name="email" value={formData.email} onChange={handleChange} type="email" />
              </div>
            ) : (
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    {applicant.phone ? <>{applicant.countryCode || '+91'} {applicant.phone}<WhatsAppButton countryCode={applicant.countryCode} phone={applicant.phone} size="md" /></> : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone 2</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    {applicant.phone2 ? <>{applicant.countryCode2 || '+91'} {applicant.phone2}<WhatsAppButton countryCode={applicant.countryCode2} phone={applicant.phone2} size="md" /></> : '-'}
                  </dd>
                </div>
                <Field label="Email" value={applicant.email} />
              </dl>
            )}
          </div>
        </div>

        {/* Job Details */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Job Details</h2>
          </div>
          <div className="px-6 py-5">
            {editing ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <EditField label="Position" name="position" value={formData.position} onChange={handleChange} />
                <EditField label="Lead Source" name="leadSource" value={formData.leadSource} onChange={handleChange} />
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                  <select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                    {APPLICANT_STATUS_OPTIONS.map((statusOption) => (
                      <option key={statusOption.value} value={statusOption.value}>
                        {statusOption.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                <Field label="Position" value={applicant.position} />
                <Field label="Lead Source" value={applicant.leadSource} />
                <Field label="Status" value={statusLabel} />
                <Field label="Initial Msg Sent At" value={applicant.initialMsgSentAt ? new Date(applicant.initialMsgSentAt).toLocaleString() : null} />
                <Field label="Created/Import Date" value={applicant.createdAt ? new Date(applicant.createdAt).toLocaleDateString() : null} />
                <Field label="Created By" value={applicant.creator ? applicant.creator.name : null} />
              </dl>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Notes</h2>
          </div>
          <div className="px-6 py-5">
            {editing ? (
              <textarea
                name="notes"
                rows="4"
                value={formData.notes}
                onChange={handleChange}
                className={inputClass}
              />
            ) : (
              <p className="text-sm text-gray-900 whitespace-pre-wrap">{applicant.notes || '-'}</p>
            )}
          </div>
        </div>

        {/* Hired Details */}
        {isHired && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Hired Details</h2>
            </div>
            <div className="px-6 py-5">
              {editing ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <EditField label="Hire Date" name="hireDate" value={formData.hireDate} onChange={handleChange} type="date" />
                  <EditField label="Start Date" name="startDate" value={formData.startDate} onChange={handleChange} type="date" />
                  <EditField label="Salary" name="salary" value={formData.salary} onChange={handleChange} type="number" />
                  <EditField label="Department" name="department" value={formData.department} onChange={handleChange} />
                  <EditField label="Employee ID" name="employeeId" value={formData.employeeId} onChange={handleChange} />
                </div>
              ) : (
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                  <Field label="Hire Date" value={applicant.hireDate ? new Date(applicant.hireDate).toLocaleDateString() : null} />
                  <Field label="Start Date" value={applicant.startDate ? new Date(applicant.startDate).toLocaleDateString() : null} />
                  <Field label="Salary" value={applicant.salary} />
                  <Field label="Department" value={applicant.department} />
                  <Field label="Employee ID" value={applicant.employeeId} />
                </dl>
              )}
            </div>
          </div>
        )}

        {/* Activity History (Super Admin only) */}
        {user?.isSuperAdmin && activityLogs.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Activity History</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {activityLogs.map((log) => (
                <div key={log.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      log.action.includes('merge') ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {log.action.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(log.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{log.details}</p>
                  {log.user && (
                    <p className="text-xs text-gray-500 mt-1">By: {log.user.name}</p>
                  )}
                  {log.changedFields && Object.keys(log.changedFields).length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Changed:</span>{' '}
                      {Object.entries(log.changedFields).map(([field, change]) => (
                        <span key={field} className="mr-2">
                          {field}: {String(change.from || '(empty)')} → {String(change.to)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {user?.isSuperAdmin && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-6 border border-red-200">
            <div className="px-6 py-5 border-b border-red-100">
              <h2 className="text-lg font-medium text-red-700">Danger Zone</h2>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Delete this contact</p>
                <p className="text-sm text-gray-500">This permanently removes the applicant record.</p>
              </div>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {deleting ? 'Deleting...' : 'Delete Contact'}
              </button>
            </div>
          </div>
        )}
      </div>
      <StatusNoteModal
        isOpen={Boolean(pendingStatus)}
        status={pendingStatus}
        saving={statusSaving}
        onClose={() => {
          if (!statusSaving) setPendingStatus('');
        }}
        onSave={handleStatusNoteSave}
      />
      <HiredStatusModal
        isOpen={showHiredModal}
        applicant={applicant}
        positionOptions={positionOptions}
        saving={statusSaving}
        onClose={() => {
          if (!statusSaving) setShowHiredModal(false);
        }}
        onSave={handleHiredSave}
      />
    </Layout>
  );
};

export default ApplicantProfile;
